import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  serviceAgentService,
  ServiceAgentProfileUpdateDto,
} from '../../../services/serviceAgent.service';
import { ServiceAgentDto } from '../../../models/ServiceAgentDto';
import { getPermissionErrorMessage } from '../../../utils/apiClient';
import './ServiceAgentProfile.scss';

export function ServiceAgentProfile() {
  const [profile, setProfile] = useState<ServiceAgentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ServiceAgentProfileUpdateDto>({});

  useEffect(() => {
    serviceAgentService.getMyProfile()
      .then((p) => {
        setProfile(p);
        setForm(pickEditable(p));
      })
      .catch((err: any) => {
        const permissionError = getPermissionErrorMessage(err);
        if (permissionError) {
          toast.error(permissionError);
        } else {
          toast.error('Failed to load profile.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const initials = profile?.fullName
    ? profile.fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const onSave = async () => {
    setSaving(true);
    try {
      const updated = await serviceAgentService.updateMyProfile(form);
      setProfile(updated);
      setForm(pickEditable(updated));
      setEditing(false);
      toast.success('Profile updated');
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      toast.error(permissionError || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    if (profile) setForm(pickEditable(profile));
    setEditing(false);
  };

  const setField = (key: keyof ServiceAgentProfileUpdateDto, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const statusBadge = profile
    ? <span className={`badge ${profile.isActive ? 'bg-success' : 'bg-secondary'}`}>{profile.isActive ? 'Active' : 'Inactive'}</span>
    : null;

  return (
    <div className="sa-profile facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">

        {/* Banner — same shape as every other Service Agent page */}
        <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-2 px-4 py-2.5 flex justify-between items-center flex-wrap gap-2">
          <div>
            <h1 className="text-[16px] font-bold text-white mb-0">My Profile</h1>
            <p className="text-[12px] opacity-90 mt-1 mb-0">Your Client Executive Agent profile and details.</p>
          </div>
          {profile && !editing && (
            <button type="button" onClick={() => setEditing(true)}
                    className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors">
              <i className="bi bi-pencil me-1"></i> Edit Profile
            </button>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="py-5 text-center"><Spinner animation="border" variant="primary" /></div>
          </div>
        ) : !profile ? (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <p className="text-center text-muted py-4 mb-0">Profile not found.</p>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); onSave(); }} noValidate>

            {/* Identity strip */}
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
              <div className="sa-profile__identity">
                <div className="sa-profile__avatar" aria-hidden="true">{initials}</div>
                <div className="sa-profile__who">
                  <div className="sa-profile__name">{profile.fullName || '—'}</div>
                  <div className="sa-profile__meta">
                    <span className="sa-profile__code">{profile.agentCode}</span>
                    {profile.agentType && <span className="sa-profile__dot">·</span>}
                    {profile.agentType && <span>{profile.agentType.replace(/_/g, ' ')}</span>}
                  </div>
                </div>
                <div className="ms-auto">{statusBadge}</div>
              </div>
            </div>

            {/* Details — read-only, or the same grid as inputs when editing */}
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
              <div className="sa-profile__section-head">Profile Details</div>
              <div className="sa-profile__grid">
                {!editing ? (
                  <>
                    <Field label="Agent Type" value={profile.agentType?.replace(/_/g, ' ')} />
                    <Field label="Email (login)" value={profile.email} wide />
                    <Field label="Mobile" value={profile.mobile} />
                    <Field label="Assigned Region" value={profile.assignedRegion} />
                    <Field label="Assigned Segment" value={profile.assignedSegment} />
                    <Field label="Onboarding Status" value={profile.onboardingStatus} />
                    <Field label="PAN Number" value={(profile as any).panNumber} />
                    <Field label="Registration Number" value={(profile as any).registrationNumber} />
                  </>
                ) : (
                  <>
                    <Input id="fullName" label="Full Name" value={form.fullName} onChange={(v) => setField('fullName', v)} />
                    <div className="form-group form-group--wide">
                      <label htmlFor="email">Email (login)</label>
                      <input id="email" type="email" className="form-control" value={profile.email || ''} readOnly data-keep-case />
                      <div className="form-text">Email cannot be changed from here. Contact an administrator.</div>
                    </div>
                    <Input id="mobile" label="Mobile" value={form.mobile} inputMode="numeric"
                           onChange={(v) => setField('mobile', v.replace(/[^0-9+]/g, ''))} />
                    <Input id="agentType" label="Agent Type" value={form.agentType} onChange={(v) => setField('agentType', v)} />
                    <Input id="assignedRegion" label="Assigned Region" value={form.assignedRegion} onChange={(v) => setField('assignedRegion', v)} />
                    <Input id="assignedSegment" label="Assigned Segment" value={form.assignedSegment} onChange={(v) => setField('assignedSegment', v)} />
                    <Input id="panNumber" label="PAN Number" value={form.panNumber} maxLength={10}
                           onChange={(v) => setField('panNumber', v.toUpperCase())} />
                    <Input id="registrationNumber" label="Registration Number" value={form.registrationNumber}
                           onChange={(v) => setField('registrationNumber', v)} />
                  </>
                )}
              </div>
            </div>

            {/* Actions — only while editing, same bar as the Introduce form */}
            {editing && (
              <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-3 py-2 mb-2 d-flex align-items-center flex-wrap gap-2">
                <div className="text-[10.5px] text-slate-500 me-auto">
                  Changes apply to your Facilon profile only. Your Power Apps contact is unaffected.
                </div>
                <button type="button" className="btn btn-sm btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                  {saving ? (<><Spinner as="span" animation="border" size="sm" /> Saving…</>) : 'Save'}
                </button>
              </div>
            )}
          </form>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Presentational helpers — same field metrics as the Introduce Investor pages
// ─────────────────────────────────────────────────────────────────────────

function Field({ label, value, wide }: { label: string; value?: string | null; wide?: boolean }) {
  return (
    <div className={`sa-profile__field${wide ? ' sa-profile__field--wide' : ''}`}>
      <div className="sa-profile__label">{label}</div>
      <div className="sa-profile__value">{value || <span className="text-muted">—</span>}</div>
    </div>
  );
}

function Input({
  id, label, value, onChange, inputMode, maxLength,
}: {
  id: string;
  label: string;
  value?: string;
  onChange: (v: string) => void;
  inputMode?: 'numeric' | 'text';
  maxLength?: number;
}) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        className="form-control"
        value={value ?? ''}
        inputMode={inputMode}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function pickEditable(p: ServiceAgentDto): ServiceAgentProfileUpdateDto {
  return {
    fullName: p.fullName,
    mobile: p.mobile,
    assignedRegion: p.assignedRegion,
    assignedSegment: p.assignedSegment,
    photoUrl: (p as any).photoUrl,
    panNumber: (p as any).panNumber,
    addressProofUrl: (p as any).addressProofUrl,
    registrationNumber: (p as any).registrationNumber,
    agentType: p.agentType,
  };
}

export default ServiceAgentProfile;
